import { Button } from "@/components/ui/button";
import VerdictBadge from "@/components/VerdictBadge";
import { Sparkles, RefreshCw, Loader2, ShieldAlert, TrendingUp, AlertTriangle } from "lucide-react";
import { useFmt } from "@/lib/useFmt";

const SEVERITY_STYLES = {
  high: "border-red-200 bg-red-50/40",
  medium: "border-amber-200 bg-amber-50/40",
  low: "border-emerald-200 bg-emerald-50/40",
};

const SEVERITY_TEXT = {
  high: "text-red-700",
  medium: "text-amber-700",
  low: "text-emerald-700",
};

function Skeleton() {
  return (
    <div className="space-y-4">
      <div className="h-6 w-2/3 animate-pulse rounded bg-slate-200" />
      <div className="h-4 w-full animate-pulse rounded bg-slate-200" />
      <div className="h-4 w-5/6 animate-pulse rounded bg-slate-200" />
      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-32 animate-pulse rounded-xl bg-slate-200" />
        ))}
      </div>
    </div>
  );
}

export default function AiAnalysisTab({ deal, onRun, running }) {
  const ai = deal?.ai_summary;
  const fmt = useFmt();

  if (running && !ai) {
    return (
      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="flex items-center gap-2 text-sm font-medium text-indigo-600">
          <Loader2 className="h-4 w-4 animate-spin" /> AI is analyzing this deal…
        </div>
        <Skeleton />
      </div>
    );
  }

  if (!ai) {
    return (
      <div
        data-testid="ai-tab-empty"
        className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center"
      >
        <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
          <Sparkles className="h-6 w-6" />
        </div>
        <h3 className="text-xl font-semibold text-slate-900">AI Analyst</h3>
        <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
          Full investor-grade breakdown — verdict, top risks, opportunities, negotiation play, and
          a closing conclusion.
        </p>
        <Button
          onClick={onRun}
          data-testid="ai-tab-run"
          className="mt-6 bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
        >
          <Sparkles className="mr-2 h-4 w-4" /> Run AI Analysis
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-tab-content">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <VerdictBadge verdict={ai.verdict} size="lg" />
            <h2 className="mt-3 text-xl font-bold tracking-tight text-slate-900">{ai.headline}</h2>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">{ai.main_reason}</p>
          </div>
          <Button
            variant="outline"
            onClick={onRun}
            disabled={running}
            data-testid="ai-tab-regenerate"
            className="border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            {running ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Regenerating…
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" /> Regenerate
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Risks */}
      <section>
        <h3 className="mb-3 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
          <ShieldAlert className="h-4 w-4" /> Top risks
        </h3>
        <div className="grid gap-4 sm:grid-cols-3">
          {(ai.top_risks || []).map((r, i) => (
            <div
              key={i}
              data-testid={`ai-risk-${i}`}
              className={`rounded-xl border-l-4 border bg-white p-5 shadow-sm ${SEVERITY_STYLES[r.severity] || "border-slate-200"}`}
            >
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-slate-900">{r.title}</h4>
                <span className={`text-[10px] font-semibold uppercase tracking-wider ${SEVERITY_TEXT[r.severity] || "text-slate-500"}`}>
                  {r.severity}
                </span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{r.detail}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Opportunities */}
      <section>
        <h3 className="mb-3 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-700">
          <TrendingUp className="h-4 w-4" /> Top opportunities
        </h3>
        <div className="grid gap-4 sm:grid-cols-3">
          {(ai.top_opportunities || []).map((o, i) => (
            <div
              key={i}
              data-testid={`ai-opp-${i}`}
              className="rounded-xl border border-emerald-200 bg-emerald-50/30 p-5 shadow-sm"
            >
              <h4 className="font-semibold text-slate-900">{o.title}</h4>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{o.detail}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Suggested offer & strategy */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-6 shadow-sm">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700">Suggested offer</div>
          <div className="mt-1 text-3xl font-bold tracking-tight text-slate-900 tabular-nums" data-testid="ai-offer-headline">
            {fmt.money(ai.suggested_offer_price)}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {(() => {
              // Batch AY — anchor "vs ask" on the ORIGINAL asking
              // (pre-counter), not on `deal.purchase_price` which is
              // the EFFECTIVE post-counter price since Batch AJ.
              const ask = deal.original_purchase_price || deal.purchase_price || 0;
              if (!ask || !ai.suggested_offer_price) return null;
              const deltaPct = ((ai.suggested_offer_price - ask) / ask) * 100;
              return (
                <>vs. ask {fmt.money(ask)} ({deltaPct.toFixed(1)}%)</>
              );
            })()}
          </div>
        </div>
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Negotiation strategy</div>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">{ai.negotiation_strategy}</p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Financing recommendation</div>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">{ai.financing_recommendation}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Rehab recommendation</div>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">{ai.rehab_recommendation}</p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 shadow-sm sm:p-8">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Investor-grade conclusion</div>
        <p className="mt-3 text-base leading-relaxed text-slate-800">{ai.investor_grade_conclusion}</p>
      </div>

      {ai.ai_status === "fallback" && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <AlertTriangle className="mr-1.5 inline h-4 w-4" />
          AI service unavailable — this is the deterministic baseline. Regenerate to retry.
        </div>
      )}
    </div>
  );
}
